import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import ModuleLayout from '../components/ModuleLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import {
  Mic, BarChart3, MessageSquare, TrendingUp, Clock, ShoppingCart,
  Brain, Shield, Loader2, RefreshCw, ChevronLeft, ChevronRight,
  Star, AlertTriangle, Lightbulb, ArrowUpRight, Users, Zap,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell,
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TABS = [
  { id: 'overview', label: 'نظرة عامة', icon: BarChart3 },
  { id: 'conversations', label: 'المحادثات', icon: MessageSquare },
  { id: 'insights', label: 'التعلّم الذكي', icon: Brain },
  { id: 'settings', label: 'الإعدادات', icon: Zap },
  { id: 'pdpl', label: 'الخصوصية', icon: Shield },
];

const GREETING_STYLES = [
  { id: 'friendly', label: 'ودود', desc: 'يا هلا! وش تحب تطلب؟' },
  { id: 'formal', label: 'رسمي', desc: 'أهلاً بكم، كيف أساعدكم؟' },
  { id: 'casual', label: 'عفوي', desc: 'هلا والله! تبي شي حلو؟' },
  { id: 'enthusiastic', label: 'حماسي', desc: 'يا مرحبا! عندنا أشياء لذيذة!' },
];

const VOICE_OPTIONS = [
  { id: 'coral', label: 'Coral', desc: 'صوت أنثوي دافئ وطبيعي', gender: 'أنثى' },
  { id: 'verse', label: 'Verse', desc: 'صوت ذكوري هادئ ومحترف', gender: 'ذكر' },
  { id: 'alloy', label: 'Alloy', desc: 'صوت محايد ومتوازن', gender: 'محايد' },
  { id: 'echo', label: 'Echo', desc: 'صوت ذكوري واثق وقوي', gender: 'ذكر' },
  { id: 'shimmer', label: 'Shimmer', desc: 'صوت أنثوي مرح ومشرق', gender: 'أنثى' },
  { id: 'sage', label: 'Sage', desc: 'صوت هادئ وحكيم', gender: 'محايد' },
];

const PERSONA_OPTIONS = [
  { id: 'balanced', label: 'متوازن', desc: 'مهني وودود', icon: '⚖️' },
  { id: 'cheerful', label: 'مرح', desc: 'حيوي ومتحمس', icon: '✨' },
  { id: 'professional', label: 'احترافي', desc: 'رسمي ومنظم', icon: '👔' },
  { id: 'humorous', label: 'فكاهي', desc: 'خفيف الظل', icon: '😄' },
];

const CONV_FILTERS = [
  { id: 'all', label: 'الكل' },
  { id: 'with_order', label: 'بطلبات' },
  { id: 'no_order', label: 'بدون طلب' },
  { id: 'satisfied', label: 'راضي' },
  { id: 'unsatisfied', label: 'غير راضي' },
];

const PIE_COLORS = ['#D97706', '#0EA5E9', '#10B981', '#EF4444', '#8B5CF6'];

function StatCard({ icon: Icon, label, value, sub, color = '#D97706' }) {
  return (
    <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs text-muted-foreground mb-1">{label}</p>
            <p className="text-2xl font-bold" style={{ color }}>{value}</p>
            {sub && <p className="text-[11px] text-muted-foreground mt-1">{sub}</p>}
          </div>
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${color}12` }}>
            <Icon className="w-5 h-5" style={{ color }} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function VoiceAgentDashboard() {
  const { authHeaders, activeBranch } = useAuth();
  const [tab, setTab] = useState('overview');
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [convsLoading, setConvsLoading] = useState(false);
  const [convsPage, setConvsPage] = useState(1);
  const [convsTotal, setConvsTotal] = useState(0);
  const [convsPages, setConvsPages] = useState(1);
  const [convsFilter, setConvsFilter] = useState('all');
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [settings, setSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedConv, setSelectedConv] = useState(null);
  const [convDetailLoading, setConvDetailLoading] = useState(false);

  const branchId = activeBranch?.id;

  // Fetch analytics
  const fetchAnalytics = async () => {
    if (!branchId) return;
    setAnalyticsLoading(true);
    try {
      const res = await axios.get(`${API}/voice-agent/analytics?branch_id=${branchId}&days=30`, authHeaders());
      setAnalytics(res.data);
    } catch { toast.error('فشل تحميل التحليلات'); }
    finally { setAnalyticsLoading(false); }
  };

  // Fetch conversations
  const fetchConversations = async (page = 1, filter = convsFilter) => {
    if (!branchId) return;
    setConvsLoading(true);
    let params = `branch_id=${branchId}&page=${page}&limit=15`;
    if (filter === 'with_order') params += '&has_order=true';
    else if (filter === 'no_order') params += '&has_order=false';
    else if (filter === 'satisfied') params += '&satisfied=true';
    else if (filter === 'unsatisfied') params += '&satisfied=false';
    try {
      const res = await axios.get(`${API}/voice-agent/conversations?${params}`, authHeaders());
      setConversations(res.data.conversations);
      setConvsTotal(res.data.total);
      setConvsPages(res.data.pages);
      setConvsPage(page);
    } catch { toast.error('فشل تحميل المحادثات'); }
    finally { setConvsLoading(false); }
  };

  // Fetch insights
  const fetchInsights = async () => {
    if (!branchId) return;
    setInsightsLoading(true);
    try {
      const res = await axios.get(`${API}/voice-agent/insights?branch_id=${branchId}`, authHeaders());
      setInsights(res.data.insights);
    } catch { toast.error('فشل تحميل التحليلات الذكية'); }
    finally { setInsightsLoading(false); }
  };

  // Fetch settings
  const fetchSettings = async () => {
    if (!branchId) return;
    setSettingsLoading(true);
    try {
      const res = await axios.get(`${API}/voice-agent/settings?branch_id=${branchId}`, authHeaders());
      setSettings(res.data);
    } catch { setSettings({ upselling_enabled: true, greeting_style: 'friendly', complaint_escalation: true, auto_language_switch: true, max_conversation_duration: 300, pdpl_consent_required: true, data_retention_days: 90, upselling_rules: [] }); }
    finally { setSettingsLoading(false); }
  };

  // Fetch conversation detail
  const fetchConvDetail = async (id) => {
    setConvDetailLoading(true);
    try {
      const res = await axios.get(`${API}/voice-agent/conversations/${id}`, authHeaders());
      setSelectedConv(res.data);
    } catch { toast.error('فشل تحميل المحادثة'); }
    finally { setConvDetailLoading(false); }
  };

  // Save settings
  const saveSettings = async () => {
    if (!branchId || !settings) return;
    setSaving(true);
    try {
      await axios.put(`${API}/voice-agent/settings?branch_id=${branchId}`, settings, authHeaders());
      toast.success('تم حفظ الإعدادات');
    } catch { toast.error('فشل حفظ الإعدادات'); }
    finally { setSaving(false); }
  };

  // Purge old data
  const purgeData = async () => {
    if (!branchId) return;
    try {
      const res = await axios.delete(`${API}/voice-agent/conversations/purge?branch_id=${branchId}`, authHeaders());
      toast.success(`تم حذف ${res.data.deleted_count} محادثة قديمة`);
      fetchConversations();
    } catch { toast.error('فشل حذف البيانات'); }
  };

  useEffect(() => {
    if (branchId) { fetchAnalytics(); fetchSettings(); }
  }, [branchId]);

  useEffect(() => {
    if (tab === 'conversations' && branchId) fetchConversations();
    if (tab === 'insights' && branchId) fetchInsights();
  }, [tab, branchId]);

  // Add upselling rule
  const addUpsellRule = () => {
    setSettings(prev => ({
      ...prev,
      upselling_rules: [...(prev.upselling_rules || []), { trigger: '', suggest: '', reason: '' }],
    }));
  };
  const updateUpsellRule = (idx, field, value) => {
    setSettings(prev => {
      const rules = [...(prev.upselling_rules || [])];
      rules[idx] = { ...rules[idx], [field]: value };
      return { ...prev, upselling_rules: rules };
    });
  };
  const removeUpsellRule = (idx) => {
    setSettings(prev => ({
      ...prev,
      upselling_rules: (prev.upselling_rules || []).filter((_, i) => i !== idx),
    }));
  };

  if (!branchId) return (
    <ModuleLayout moduleKey="ai">
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
        <Mic className="w-12 h-12 text-muted-foreground mb-4" />
        <h2 className="text-lg font-semibold">اختر فرعاً لإدارة الوكيل الصوتي</h2>
      </div>
    </ModuleLayout>
  );

  return (
    <ModuleLayout moduleKey="ai">
      <div className="space-y-4" data-testid="voice-agent-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-bold text-xl md:text-2xl text-foreground flex items-center gap-2" style={{ fontFamily: 'Cairo, sans-serif' }}>
              <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-amber-500/10">
                <Mic className="w-5 h-5 text-amber-600" />
              </div>
              الوكيل الصوتي الذكي
            </h1>
            <p className="text-sm text-muted-foreground mt-1">إدارة وتحليل وتعلّم الوكيل الصوتي</p>
          </div>
          <Button
            variant="outline" size="sm"
            onClick={fetchAnalytics}
            disabled={analyticsLoading}
            className="rounded-full gap-1.5 text-xs h-8"
            data-testid="refresh-analytics-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${analyticsLoading ? 'animate-spin' : ''}`} /> تحديث
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-muted/50 rounded-xl p-1 overflow-x-auto">
          {TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`tab-${t.id}`}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${
                tab === t.id ? 'bg-white shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          ))}
        </div>

        {/* ══════════ Overview Tab ══════════ */}
        {tab === 'overview' && (
          <div className="space-y-4">
            {analyticsLoading ? (
              <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-amber-600" /></div>
            ) : analytics ? (
              <>
                {/* Stat Cards */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="analytics-stats">
                  <StatCard icon={MessageSquare} label="المحادثات" value={analytics.total_conversations} sub="آخر 30 يوم" />
                  <StatCard icon={TrendingUp} label="معدل التحويل" value={`${analytics.conversion_rate}%`} sub={`${analytics.conversations_with_orders} طلب`} color="#10B981" />
                  <StatCard icon={Star} label="رضا العملاء" value={`${analytics.satisfaction_rate}%`} color={analytics.satisfaction_rate >= 70 ? '#10B981' : '#EF4444'} />
                  <StatCard icon={Clock} label="متوسط المدة" value={`${Math.round(analytics.avg_duration_seconds / 60)}د`} sub={`${analytics.avg_duration_seconds}ث`} color="#0EA5E9" />
                  <StatCard icon={ShoppingCart} label="الإيرادات" value={`${analytics.total_revenue?.toFixed(0)} ر.س`} color="#8B5CF6" />
                </div>

                {/* Charts Row */}
                <div className="grid md:grid-cols-2 gap-4">
                  {/* Daily Trend */}
                  <Card className="border-0 shadow-sm">
                    <CardContent className="p-4">
                      <h3 className="text-sm font-semibold mb-3">المحادثات اليومية</h3>
                      {analytics.daily_trend?.length > 0 ? (
                        <ResponsiveContainer width="100%" height={200}>
                          <AreaChart data={analytics.daily_trend}>
                            <defs>
                              <linearGradient id="vcFill" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#D97706" stopOpacity={0.3} />
                                <stop offset="95%" stopColor="#D97706" stopOpacity={0} />
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                            <YAxis tick={{ fontSize: 10 }} />
                            <Tooltip />
                            <Area type="monotone" dataKey="conversations" stroke="#D97706" fill="url(#vcFill)" strokeWidth={2} />
                            <Area type="monotone" dataKey="orders" stroke="#10B981" fill="transparent" strokeWidth={1.5} strokeDasharray="4 4" />
                          </AreaChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="text-center py-8 text-muted-foreground text-sm">لا توجد بيانات بعد</div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Popular Items */}
                  <Card className="border-0 shadow-sm">
                    <CardContent className="p-4">
                      <h3 className="text-sm font-semibold mb-3">الأصناف الأكثر طلباً بالصوت</h3>
                      {analytics.popular_items?.length > 0 ? (
                        <ResponsiveContainer width="100%" height={200}>
                          <BarChart data={analytics.popular_items.slice(0, 6)} layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis type="number" tick={{ fontSize: 10 }} />
                            <YAxis dataKey="name" type="category" width={80} tick={{ fontSize: 10 }} />
                            <Tooltip />
                            <Bar dataKey="count" fill="#D97706" radius={[0, 4, 4, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="text-center py-8 text-muted-foreground text-sm">لا توجد طلبات بعد</div>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Language Distribution + Top Pairs */}
                <div className="grid md:grid-cols-2 gap-4">
                  <Card className="border-0 shadow-sm">
                    <CardContent className="p-4">
                      <h3 className="text-sm font-semibold mb-3">توزيع اللغات</h3>
                      {analytics.languages?.length > 0 ? (
                        <div className="flex items-center gap-4">
                          <ResponsiveContainer width={120} height={120}>
                            <PieChart>
                              <Pie data={analytics.languages} dataKey="count" nameKey="language" cx="50%" cy="50%" outerRadius={50} innerRadius={30}>
                                {analytics.languages.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                              </Pie>
                            </PieChart>
                          </ResponsiveContainer>
                          <div className="flex flex-col gap-2">
                            {analytics.languages.map((l, i) => (
                              <div key={i} className="flex items-center gap-2 text-xs">
                                <div className="w-3 h-3 rounded-sm" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                                <span>{l.language === 'ar' ? 'عربي' : l.language === 'en' ? 'إنجليزي' : l.language}</span>
                                <span className="text-muted-foreground">({l.count})</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-8 text-muted-foreground text-sm">لا توجد بيانات</div>
                      )}
                    </CardContent>
                  </Card>

                  <Card className="border-0 shadow-sm">
                    <CardContent className="p-4">
                      <h3 className="text-sm font-semibold mb-3 flex items-center gap-1.5">
                        <Lightbulb className="w-4 h-4 text-amber-500" /> أزواج الأصناف الشائعة
                      </h3>
                      {analytics.top_pairs?.length > 0 ? (
                        <div className="space-y-2">
                          {analytics.top_pairs.map((p, i) => (
                            <div key={i} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                              <span className="text-sm">{p.pair}</span>
                              <Badge variant="secondary" className="text-[10px]">{p.count}x</Badge>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-muted-foreground text-sm">تحتاج محادثات أكثر لتحليل الأزواج</div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </>
            ) : (
              <div className="text-center py-16 text-muted-foreground">
                <Mic className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>لا توجد بيانات بعد. ستظهر التحليلات بعد أول محادثة صوتية.</p>
              </div>
            )}
          </div>
        )}

        {/* ══════════ Conversations Tab ══════════ */}
        {tab === 'conversations' && (
          <div className="space-y-4" data-testid="conversations-list">
            {selectedConv ? (
              <div>
                <Button variant="ghost" size="sm" onClick={() => setSelectedConv(null)} className="mb-3 gap-1.5" data-testid="back-to-list-btn">
                  <ChevronRight className="w-4 h-4" /> العودة للقائمة
                </Button>
                <Card className="border-0 shadow-sm">
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-sm">تفاصيل المحادثة</h3>
                      <div className="flex gap-2">
                        <Badge variant={selectedConv.has_order ? 'default' : 'secondary'}>
                          {selectedConv.has_order ? 'تم الطلب' : 'بدون طلب'}
                        </Badge>
                        <Badge variant="outline">{selectedConv.language === 'ar' ? 'عربي' : 'English'}</Badge>
                        {selectedConv.customer_satisfied === true && <Badge className="bg-green-100 text-green-700 border-0">راضي</Badge>}
                        {selectedConv.customer_satisfied === false && <Badge className="bg-red-100 text-red-700 border-0">غير راضي</Badge>}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3 text-xs">
                      <div className="bg-muted/50 rounded-lg p-3 text-center">
                        <Clock className="w-4 h-4 mx-auto mb-1 text-muted-foreground" />
                        <p className="font-medium">{Math.round((selectedConv.duration_seconds || 0) / 60)}د</p>
                        <p className="text-muted-foreground">المدة</p>
                      </div>
                      <div className="bg-muted/50 rounded-lg p-3 text-center">
                        <ShoppingCart className="w-4 h-4 mx-auto mb-1 text-muted-foreground" />
                        <p className="font-medium">{selectedConv.cart_items?.length || 0}</p>
                        <p className="text-muted-foreground">أصناف</p>
                      </div>
                      <div className="bg-muted/50 rounded-lg p-3 text-center">
                        <TrendingUp className="w-4 h-4 mx-auto mb-1 text-muted-foreground" />
                        <p className="font-medium">{selectedConv.total_amount?.toFixed(0) || 0} ر.س</p>
                        <p className="text-muted-foreground">الإجمالي</p>
                      </div>
                    </div>
                    {/* Conversation events */}
                    <div className="space-y-2 max-h-[400px] overflow-y-auto">
                      {(selectedConv.events || []).map((evt, i) => (
                        <div key={i} className={`p-2.5 rounded-xl text-sm ${
                          evt.role === 'user' ? 'bg-blue-50 text-blue-900 mr-8'
                            : evt.role === 'system' ? 'bg-red-50 text-red-800 mx-4 text-center text-xs'
                            : 'bg-amber-50 text-amber-900 ml-8'
                        }`}>
                          <span className="text-[10px] font-medium text-muted-foreground block mb-0.5">
                            {evt.role === 'user' ? 'العميل' : evt.role === 'system' ? 'نظام' : 'الوكيل'}
                          </span>
                          {evt.text_anonymized || evt.text || '—'}
                        </div>
                      ))}
                      {(!selectedConv.events || selectedConv.events.length === 0) && (
                        <p className="text-center text-muted-foreground text-sm py-4">لا توجد أحداث مسجلة</p>
                      )}
                    </div>
                    {selectedConv.cart_items?.length > 0 && (
                      <div className="border-t pt-3">
                        <h4 className="text-xs font-semibold mb-2">الطلب</h4>
                        {selectedConv.cart_items.map((item, i) => (
                          <div key={i} className="flex justify-between text-sm py-1">
                            <span>{item.name} x{item.quantity}</span>
                            <span className="font-medium">{(item.price * item.quantity).toFixed(0)} ر.س</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            ) : (
              <>
                {/* Filters */}
                <div className="flex gap-1.5 overflow-x-auto pb-1" data-testid="conversation-filters">
                  {CONV_FILTERS.map(f => (
                    <button
                      key={f.id}
                      onClick={() => { setConvsFilter(f.id); fetchConversations(1, f.id); }}
                      data-testid={`filter-${f.id}`}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                        convsFilter === f.id
                          ? 'bg-amber-100 text-amber-800 shadow-sm'
                          : 'bg-muted/50 text-muted-foreground hover:bg-muted'
                      }`}
                    >
                      {f.label}
                      {f.id === 'all' && convsTotal > 0 && <span className="mr-1 text-[10px] opacity-60">({convsTotal})</span>}
                    </button>
                  ))}
                </div>
                {convsLoading ? (
                  <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-amber-600" /></div>
                ) : conversations.length > 0 ? (
                  <>
                    <div className="space-y-2">
                      {conversations.map(c => (
                        <Card
                          key={c.id}
                          className="border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
                          onClick={() => fetchConvDetail(c.id)}
                          data-testid={`conv-item-${c.id}`}
                        >
                          <CardContent className="p-3 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`w-9 h-9 rounded-full flex items-center justify-center ${c.has_order ? 'bg-green-100' : 'bg-gray-100'}`}>
                                {c.has_order ? <ShoppingCart className="w-4 h-4 text-green-600" /> : <MessageSquare className="w-4 h-4 text-gray-500" />}
                              </div>
                              <div>
                                <p className="text-sm font-medium">
                                  {c.has_order ? `طلب - ${c.total_amount?.toFixed(0) || 0} ر.س` : 'محادثة بدون طلب'}
                                </p>
                                <p className="text-[11px] text-muted-foreground">
                                  {new Date(c.created_at).toLocaleDateString('ar-SA', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                  {' - '}
                                  {Math.round((c.duration_seconds || 0) / 60)}د
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="text-[10px]">{c.language === 'ar' ? 'عربي' : 'EN'}</Badge>
                              {c.customer_satisfied === true && <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />}
                              {c.customer_satisfied === false && <AlertTriangle className="w-3.5 h-3.5 text-red-500" />}
                              <ChevronLeft className="w-4 h-4 text-muted-foreground" />
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                    {/* Pagination */}
                    {convsPages > 1 && (
                      <div className="flex items-center justify-center gap-2 pt-2">
                        <Button variant="outline" size="sm" disabled={convsPage <= 1} onClick={() => fetchConversations(convsPage - 1)} className="h-8">
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                        <span className="text-xs text-muted-foreground">{convsPage} / {convsPages}</span>
                        <Button variant="outline" size="sm" disabled={convsPage >= convsPages} onClick={() => fetchConversations(convsPage + 1)} className="h-8">
                          <ChevronLeft className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-16 text-muted-foreground">
                    <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>لا توجد محادثات مسجلة بعد</p>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ══════════ Insights Tab ══════════ */}
        {tab === 'insights' && (
          <div className="space-y-4" data-testid="insights-tab">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold flex items-center gap-1.5">
                <Brain className="w-4 h-4 text-purple-500" /> التعلّم من المحادثات
              </h3>
              <Button variant="outline" size="sm" onClick={fetchInsights} disabled={insightsLoading} className="h-7 text-xs rounded-full gap-1" data-testid="refresh-insights-btn">
                <RefreshCw className={`w-3 h-3 ${insightsLoading ? 'animate-spin' : ''}`} /> تحديث
              </Button>
            </div>

            {insightsLoading ? (
              <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-purple-500" /></div>
            ) : insights ? (
              <>
                {/* Summary */}
                <Card className="border-0 shadow-sm border-r-4 border-r-purple-500">
                  <CardContent className="p-4">
                    <p className="text-sm leading-relaxed">{insights.summary}</p>
                  </CardContent>
                </Card>

                {/* Recommendations */}
                {insights.recommendations?.length > 0 && (
                  <Card className="border-0 shadow-sm">
                    <CardContent className="p-4">
                      <h4 className="text-xs font-semibold mb-3 flex items-center gap-1.5">
                        <Lightbulb className="w-4 h-4 text-amber-500" /> توصيات التحسين
                      </h4>
                      <div className="space-y-2">
                        {insights.recommendations.map((r, i) => (
                          <div key={i} className="flex items-start gap-2 text-sm">
                            <ArrowUpRight className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                            <span>{r}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Upsell Suggestions */}
                {insights.upsell_suggestions?.length > 0 && (
                  <Card className="border-0 shadow-sm">
                    <CardContent className="p-4">
                      <h4 className="text-xs font-semibold mb-3 flex items-center gap-1.5">
                        <TrendingUp className="w-4 h-4 text-green-500" /> اقتراحات Upselling ذكية
                      </h4>
                      <div className="space-y-2">
                        {insights.upsell_suggestions.map((s, i) => (
                          <div key={i} className="bg-green-50 rounded-lg p-3 text-sm">
                            <span className="font-medium">إذا طلب "{s.trigger}"</span>
                            <span className="text-muted-foreground"> → اقترح </span>
                            <span className="font-medium text-green-700">"{s.suggest}"</span>
                            {s.reason && <p className="text-xs text-muted-foreground mt-1">{s.reason}</p>}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Strengths & Improvements */}
                <div className="grid md:grid-cols-2 gap-4">
                  {insights.strengths?.length > 0 && (
                    <Card className="border-0 shadow-sm">
                      <CardContent className="p-4">
                        <h4 className="text-xs font-semibold mb-2 text-green-600">نقاط القوة</h4>
                        {insights.strengths.map((s, i) => (
                          <p key={i} className="text-sm flex items-start gap-1.5 py-1"><span className="text-green-500 mt-0.5">+</span> {s}</p>
                        ))}
                      </CardContent>
                    </Card>
                  )}
                  {insights.improvements?.length > 0 && (
                    <Card className="border-0 shadow-sm">
                      <CardContent className="p-4">
                        <h4 className="text-xs font-semibold mb-2 text-amber-600">نقاط التحسين</h4>
                        {insights.improvements.map((s, i) => (
                          <p key={i} className="text-sm flex items-start gap-1.5 py-1"><span className="text-amber-500 mt-0.5">!</span> {s}</p>
                        ))}
                      </CardContent>
                    </Card>
                  )}
                </div>
              </>
            ) : (
              <div className="text-center py-16 text-muted-foreground">
                <Brain className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>لا توجد بيانات كافية. يلزم 3 محادثات على الأقل لبدء التعلّم.</p>
              </div>
            )}
          </div>
        )}

        {/* ══════════ Settings Tab ══════════ */}
        {tab === 'settings' && (
          <div className="space-y-4" data-testid="settings-tab">
            {settingsLoading ? (
              <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin" /></div>
            ) : settings ? (
              <>
                {/* Voice Selection */}
                <Card className="border-0 shadow-sm">
                  <CardContent className="p-4">
                    <h4 className="text-sm font-semibold mb-3 flex items-center gap-1.5">
                      <Mic className="w-4 h-4 text-amber-500" /> صوت الوكيل
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {VOICE_OPTIONS.map(v => (
                        <button
                          key={v.id}
                          onClick={() => setSettings(prev => ({ ...prev, voice_id: v.id }))}
                          data-testid={`voice-${v.id}`}
                          className={`p-3 rounded-xl border text-right transition-all ${
                            settings.voice_id === v.id
                              ? 'border-amber-400 bg-amber-50 shadow-sm'
                              : 'border-border hover:border-amber-200'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-semibold">{v.label}</span>
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">{v.gender}</span>
                          </div>
                          <p className="text-[11px] text-muted-foreground">{v.desc}</p>
                        </button>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Persona */}
                <Card className="border-0 shadow-sm">
                  <CardContent className="p-4">
                    <h4 className="text-sm font-semibold mb-3 flex items-center gap-1.5">
                      <Users className="w-4 h-4 text-purple-500" /> شخصية الوكيل
                    </h4>
                    <div className="grid grid-cols-2 gap-2">
                      {PERSONA_OPTIONS.map(p => (
                        <button
                          key={p.id}
                          onClick={() => setSettings(prev => ({ ...prev, persona: p.id }))}
                          data-testid={`persona-${p.id}`}
                          className={`p-3 rounded-xl border text-right transition-all ${
                            settings.persona === p.id
                              ? 'border-purple-400 bg-purple-50 shadow-sm'
                              : 'border-border hover:border-purple-200'
                          }`}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-base">{p.icon}</span>
                            <span className="text-sm font-medium">{p.label}</span>
                          </div>
                          <p className="text-[11px] text-muted-foreground">{p.desc}</p>
                        </button>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Greeting Style */}
                <Card className="border-0 shadow-sm">
                  <CardContent className="p-4">
                    <h4 className="text-sm font-semibold mb-3">أسلوب الترحيب</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {GREETING_STYLES.map(g => (
                        <button
                          key={g.id}
                          onClick={() => setSettings(prev => ({ ...prev, greeting_style: g.id }))}
                          data-testid={`greeting-${g.id}`}
                          className={`p-3 rounded-xl border text-right transition-all ${
                            settings.greeting_style === g.id
                              ? 'border-amber-400 bg-amber-50 shadow-sm'
                              : 'border-border hover:border-amber-200'
                          }`}
                        >
                          <p className="text-sm font-medium">{g.label}</p>
                          <p className="text-[11px] text-muted-foreground mt-0.5">{g.desc}</p>
                        </button>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Custom Phrases */}
                <Card className="border-0 shadow-sm">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-semibold flex items-center gap-1.5">
                        <MessageSquare className="w-4 h-4 text-blue-500" /> عبارات مخصصة
                      </h4>
                      <Button
                        variant="outline" size="sm"
                        onClick={() => setSettings(prev => ({
                          ...prev,
                          custom_phrases: [...(prev.custom_phrases || []), { phrase: '', context: '' }],
                        }))}
                        className="h-7 text-xs"
                        data-testid="add-phrase-btn"
                      >
                        + إضافة
                      </Button>
                    </div>
                    <p className="text-[11px] text-muted-foreground mb-3">
                      أضف عبارات يستخدمها الوكيل بشكل طبيعي في المحادثة (مثال: شعارات، عروض، تعبيرات مميزة)
                    </p>
                    {(settings.custom_phrases || []).map((phrase, i) => (
                      <div key={i} className="flex gap-2 items-end mb-2">
                        <div className="flex-1">
                          <Label className="text-[10px]">العبارة</Label>
                          <Input
                            value={phrase.phrase}
                            onChange={e => {
                              const arr = [...(settings.custom_phrases || [])];
                              arr[i] = { ...arr[i], phrase: e.target.value };
                              setSettings(prev => ({ ...prev, custom_phrases: arr }));
                            }}
                            placeholder="عندنا عرض خاص اليوم!"
                            className="h-8 text-xs"
                          />
                        </div>
                        <div className="flex-1">
                          <Label className="text-[10px]">متى يستخدمها</Label>
                          <Input
                            value={phrase.context}
                            onChange={e => {
                              const arr = [...(settings.custom_phrases || [])];
                              arr[i] = { ...arr[i], context: e.target.value };
                              setSettings(prev => ({ ...prev, custom_phrases: arr }));
                            }}
                            placeholder="عند الترحيب"
                            className="h-8 text-xs"
                          />
                        </div>
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => {
                            setSettings(prev => ({
                              ...prev,
                              custom_phrases: (prev.custom_phrases || []).filter((_, j) => j !== i),
                            }));
                          }}
                          className="h-8 text-red-500 px-2"
                        >
                          x
                        </Button>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                {/* Feature Toggles */}
                <Card className="border-0 shadow-sm">
                  <CardContent className="p-4 space-y-4">
                    <h4 className="text-sm font-semibold">ميزات الوكيل</h4>
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-sm">اقتراحات Upselling</Label>
                        <p className="text-[11px] text-muted-foreground">يقترح أصناف مكمّلة تلقائياً</p>
                      </div>
                      <Switch checked={settings.upselling_enabled} onCheckedChange={v => setSettings(prev => ({ ...prev, upselling_enabled: v }))} data-testid="toggle-upselling" />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-sm">تصعيد الشكاوى</Label>
                        <p className="text-[11px] text-muted-foreground">يسجّل ويصعّد الشكاوى الحادة</p>
                      </div>
                      <Switch checked={settings.complaint_escalation} onCheckedChange={v => setSettings(prev => ({ ...prev, complaint_escalation: v }))} data-testid="toggle-complaints" />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-sm">تبديل اللغة تلقائياً</Label>
                        <p className="text-[11px] text-muted-foreground">يتحول للإنجليزية إذا تكلم العميل بها</p>
                      </div>
                      <Switch checked={settings.auto_language_switch} onCheckedChange={v => setSettings(prev => ({ ...prev, auto_language_switch: v }))} data-testid="toggle-language" />
                    </div>
                  </CardContent>
                </Card>

                {/* Upselling Rules */}
                {settings.upselling_enabled && (
                  <Card className="border-0 shadow-sm">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-semibold">قواعد Upselling مخصصة</h4>
                        <Button variant="outline" size="sm" onClick={addUpsellRule} className="h-7 text-xs" data-testid="add-upsell-rule-btn">+ إضافة</Button>
                      </div>
                      <p className="text-[11px] text-muted-foreground mb-3">حدد أزواج أصناف يقترحها الوكيل (مثال: إذا طلب برجر → اقترح بطاطس)</p>
                      {(settings.upselling_rules || []).map((rule, i) => (
                        <div key={i} className="flex gap-2 items-end mb-2">
                          <div className="flex-1">
                            <Label className="text-[10px]">إذا طلب</Label>
                            <Input value={rule.trigger} onChange={e => updateUpsellRule(i, 'trigger', e.target.value)} placeholder="برجر" className="h-8 text-xs" />
                          </div>
                          <div className="flex-1">
                            <Label className="text-[10px]">اقترح</Label>
                            <Input value={rule.suggest} onChange={e => updateUpsellRule(i, 'suggest', e.target.value)} placeholder="بطاطس" className="h-8 text-xs" />
                          </div>
                          <div className="flex-1">
                            <Label className="text-[10px]">السبب</Label>
                            <Input value={rule.reason} onChange={e => updateUpsellRule(i, 'reason', e.target.value)} placeholder="خصم 20%" className="h-8 text-xs" />
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => removeUpsellRule(i)} className="h-8 text-red-500 px-2">x</Button>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}

                <Button onClick={saveSettings} disabled={saving} className="rounded-full gap-1.5" data-testid="save-settings-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                  حفظ الإعدادات
                </Button>
              </>
            ) : null}
          </div>
        )}

        {/* ══════════ PDPL Tab ══════════ */}
        {tab === 'pdpl' && (
          <div className="space-y-4" data-testid="pdpl-tab">
            <Card className="border-0 shadow-sm border-r-4 border-r-blue-500">
              <CardContent className="p-4">
                <h4 className="text-sm font-semibold flex items-center gap-1.5 mb-2">
                  <Shield className="w-4 h-4 text-blue-500" /> التوافق مع نظام حماية البيانات الشخصية (PDPL)
                </h4>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  نظام الوكيل الصوتي مصمم ليتوافق مع نظام حماية البيانات الشخصية في المملكة العربية السعودية.
                  يتم تطبيق الإجراءات التالية تلقائياً:
                </p>
              </CardContent>
            </Card>

            <div className="grid md:grid-cols-2 gap-4">
              <Card className="border-0 shadow-sm">
                <CardContent className="p-4 space-y-3">
                  <h5 className="text-xs font-semibold text-green-600">ما يتم تطبيقه</h5>
                  {[
                    'إخفاء هوية أرقام الهاتف والأسماء في البيانات المحللة',
                    'طلب موافقة صريحة قبل تسجيل المحادثة',
                    'حذف تلقائي للبيانات بعد فترة الاحتفاظ',
                    'عدم مشاركة البيانات مع أطراف ثالثة',
                    'حق العميل في طلب حذف بياناته',
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm">
                      <div className="w-4 h-4 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-green-600 text-[10px] font-bold">&#10003;</span>
                      </div>
                      {item}
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card className="border-0 shadow-sm">
                <CardContent className="p-4 space-y-3">
                  <h5 className="text-xs font-semibold">إعدادات الخصوصية</h5>
                  {settings && (
                    <>
                      <div className="flex items-center justify-between">
                        <div>
                          <Label className="text-sm">طلب الموافقة قبل التسجيل</Label>
                          <p className="text-[11px] text-muted-foreground">يعرض إشعار خصوصية للعميل</p>
                        </div>
                        <Switch checked={settings.pdpl_consent_required} onCheckedChange={v => setSettings(prev => ({ ...prev, pdpl_consent_required: v }))} data-testid="toggle-pdpl-consent" />
                      </div>
                      <div>
                        <Label className="text-sm">فترة الاحتفاظ بالبيانات (أيام)</Label>
                        <Input type="number" value={settings.data_retention_days} onChange={e => setSettings(prev => ({ ...prev, data_retention_days: parseInt(e.target.value) || 90 }))} className="h-8 mt-1 w-24" data-testid="retention-days-input" />
                      </div>
                      <div className="pt-2 flex gap-2">
                        <Button onClick={saveSettings} disabled={saving} size="sm" className="rounded-full gap-1 text-xs" data-testid="save-pdpl-btn">
                          حفظ
                        </Button>
                        <Button variant="destructive" size="sm" onClick={purgeData} className="rounded-full gap-1 text-xs" data-testid="purge-data-btn">
                          حذف البيانات المنتهية
                        </Button>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>
    </ModuleLayout>
  );
}
